import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { OtpRequiredData } from "../services/authApi";

interface OtpVerificationFormProps {
  otpData: OtpRequiredData;
  onVerify: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  isVerifying: boolean;
  isResending: boolean;
  error?: string | null;
}

export function OtpVerificationForm({
  otpData,
  onVerify,
  onResend,
  onBack,
  isVerifying,
  isResending,
  error,
}: OtpVerificationFormProps) {
  const [digits, setDigits] = useState<string[]>(Array(otpData.codeLength).fill(""));
  const [countdown, setCountdown] = useState(() => getSecondsRemaining(otpData.expiresAt));
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getSecondsRemaining(otpData.expiresAt));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpData.expiresAt]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  function getSecondsRemaining(expiresAt: string) {
    return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  }

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);

    if (value && index < otpData.codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const code = newDigits.join("");
    if (code.length === otpData.codeLength && newDigits.every((d) => d !== "")) {
      onVerify(code);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, otpData.codeLength);
    if (!pasted) return;
    const newDigits = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);
    const nextIndex = Math.min(pasted.length, otpData.codeLength - 1);
    inputRefs.current[nextIndex]?.focus();

    if (pasted.length === otpData.codeLength) {
      onVerify(pasted);
    }
  };

  const handleResend = () => {
    setResendCooldown(60);
    setDigits(Array(otpData.codeLength).fill(""));
    onResend();
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col gap-6">
      <Card className="overflow-hidden p-0">
        <CardContent className="flex flex-col items-center gap-6 p-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">Verify your identity</h2>
            <p className="text-muted-foreground text-sm">
              We sent a {otpData.codeLength}-digit code to{" "}
              <span className="font-medium">{otpData.maskedPhone}</span>
            </p>
          </div>

          <div className="flex gap-2" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-xl font-semibold"
                disabled={isVerifying || countdown === 0}
              />
            ))}
          </div>

          {error && (
            <p className="text-destructive text-sm text-center">{error}</p>
          )}

          {countdown > 0 ? (
            <p className="text-muted-foreground text-sm">
              Code expires in <span className="font-medium">{formatTime(countdown)}</span>
            </p>
          ) : (
            <p className="text-destructive text-sm">Code expired. Please request a new one.</p>
          )}

          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className="w-full"
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : "Resend code"}
            </Button>
            <Button variant="ghost" onClick={onBack} className="w-full">
              Back to login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
